import AsyncFormInput from '@form/AsyncFormInput';
import FormWatcher from '@form/FormWatcher';
import { faDollarSign, faPlus, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useCurrency from '@hooks/useCurrency';
import useModalManager from '@hooks/useModalManager';
import { Button } from '@ui';
import { useClubSlug } from 'components/Clubs/hooks';
import { Column } from 'components/DataTable';
import { AsyncListRef } from 'components/DataTable/DataTable';
import { FormInput } from 'components/Form/next';
import { FormInputProps } from 'components/Form/next/props';
import { get } from 'lodash';
import dynamic from 'next/dynamic';
import React, { useCallback, useMemo } from 'react';
import { FieldValues, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import AccountFormInput from '../Account/AccountFormInput';
import CostCenterFormInput from '../CostCenter/CostCenterFormInput';
import { Product } from '../Products/types';

const ProductForm = dynamic(() => import('../Products/ProductForm'));

interface Props extends FormInputProps {
    allowEditing?: boolean;
    allowAddCredits?: boolean;
    showDescription?: boolean;
    showLedgerFields?: boolean;
}

export const InvoiceItemsFields = ({
    name,
    showLedgerFields,
    showDescription = false,
    allowEditing = true,
    allowAddCredits = true
}: Props) => {
    const { t } = useTranslation('invoice');
    const {
        control,
        watch,
        formState: { errors },
        setValue,
        getValues
    } = useFormContext();

    const { getSymbol } = useCurrency();
    const clubSlug = useClubSlug();
    const currencyCode = watch('currency');
    const error = get(errors, name)?.message;
    const asyncListRef = React.useRef<AsyncListRef>(undefined);
    const taxesPlaceholder = watch('taxes') || '0';
    const { fields, append, remove } = useFieldArray({
        control,
        name
    });
    const { addModal, removeModal } = useModalManager();

    const handleOpenProductForm = useCallback((data: FieldValues) => {
        addModal({
            title: t('invoiceForm.itemsFields.addProduct'),
            content: (id) => (
                <ProductForm
                    defaultValues={{
                        name: data?.description,
                        description: data?.description_long,
                        price: data?.price_per_item,
                        taxes: data?.taxes
                    }}
                    callback={() => {
                        asyncListRef.current?.reload();
                        removeModal(id);
                    }}
                />
            ),
            show: true
        });
    }, [t]);

    const inputProps: Partial<React.ComponentProps<typeof FormInput>> = { size: 'sm', flush: true };

    const columns: Column[] = useMemo(
        () => [
            {
                uid: 'name',
                name: t('invoiceForm.fields.name')
            },
            {
                uid: 'taxes',
                name: t('invoiceForm.fields.taxes'),
                renderer: (taxes) => `${taxes} %`
            },
            {
                uid: 'price',
                name: t('invoiceForm.itemsFields.price'),
                renderer: (price) => `${price} ${getSymbol(currencyCode)}`
            }
        ],
        [t, getSymbol, currencyCode]
    );

    return (
        <>
            {fields.map((field, index) => (
                <div className="tw-mb-2" key={field.id}>
                    <div className="tw-flex tw-items-baseline tw-gap-2" key={field.id}>
                        <AsyncFormInput
                            ListEmptyComponent={(close) => (
                                <div className="tw-flex tw-flex-col tw-gap-4">
                                    <span className="tw-text-accents-8">{t('invoiceForm.itemsFields.noProductsFound')}</span>
                                    <Button
                                        onPress={() => {
                                            handleOpenProductForm(get(getValues(), `${name}.${index}`));
                                            close();
                                        }}
                                        size="sm"
                                        className="tw-ml-2"
                                        icon={<FontAwesomeIcon icon={faPlus} />}
                                    >
                                        {t('invoiceForm.itemsFields.createProduct')}
                                    </Button>
                                </div>
                            )}
                            inputProps={{
                                ...inputProps,
                                placeholder: t('invoiceForm.fields.description'),
                                disabled: !allowEditing,
                                fullWidth: true
                            }}
                            onSelect={(item: Product) => {
                                setValue(`${name}.${index}.description`, item?.name);
                                setValue(`${name}.${index}.description_long`, item?.description);
                                setValue(`${name}.${index}.taxes`, item?.taxes);
                                setValue(`${name}.${index}.price_per_item`, item?.price);
                                setValue(`${name}.${index}.user_invoice_product_id`, item?.id);
                            }}
                            className="tw-w-full"
                            name={`${name}.${index}.description`}
                            keyResolver={(item: Product) => item.name}
                            tableColumns={columns}
                            fetchUrl={`invoice/${clubSlug}/product`}
                        />
                        <FormWatcher name={`${name}.${index}.user_invoice_product_id`}>
                            {(value) =>
                                !!value ? (
                                    <Button
                                        onPress={() => {
                                            setValue(`${name}.${index}.user_invoice_product_id`, undefined);
                                            setValue(`${name}.${index}.description`, undefined);
                                            setValue(`${name}.${index}.description_long`, undefined);
                                            setValue(`${name}.${index}.taxes`, undefined);
                                            setValue(`${name}.${index}.price_per_item`, undefined);
                                        }}
                                        size={'sm'}
                                        color="secondary"
                                        icon={<FontAwesomeIcon icon={faTimes} />}
                                        disabled={!allowEditing}
                                    />
                                ) : null
                            }
                        </FormWatcher>
                        <FormInput
                            {...inputProps}
                            className="tw-max-w-[50px]"
                            type="number"
                            min={-100}
                            step={0.01}
                            max={10000}
                            placeholder={t('invoiceForm.itemsFields.quantity')}
                            name={`${name}.${index}.amount`}
                            disabled={!allowEditing}
                        />
                        <FormInput
                            {...inputProps}
                            className="tw-max-w-[100px]"
                            type="number"
                            placeholder={taxesPlaceholder}
                            labelRight="%"
                            name={`${name}.${index}.taxes`}
                            disabled={!allowEditing}
                        />
                        <FormInput
                            {...inputProps}
                            className="tw-max-w-[160px]"
                            type="number"
                            min={0}
                            step={0.0001}
                            labelRight={getSymbol(currencyCode)}
                            placeholder={t('invoiceForm.fields.pricePerItem')}
                            name={`${name}.${index}.price_per_item`}
                            disabled={!allowEditing}
                        />

                        {allowAddCredits && (
                            <Button
                                onPress={() =>
                                    setValue('credits', [
                                        ...(getValues('credits') ?? []),
                                        {
                                            amount:
                                                getValues('items')[index].price_per_item *
                                                getValues('items')[index].amount,
                                            percentage: false,
                                            description: t('invoiceForm.itemsFields.creditDescription', {
                                                description: getValues('items')[index].description
                                            })
                                        }
                                    ])
                                }
                                auto
                                css={{ mt: 3, px: '$4' }}
                                size={'sm'}
                                icon={<FontAwesomeIcon icon={faDollarSign} />}
                                disabled={!allowEditing}
                            />
                        )}
                        <Button
                            onPress={() => remove(index)}
                            color="error"
                            auto
                            css={{ mt: 3, px: '$4' }}
                            size={inputProps.size}
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            disabled={!allowEditing}
                        />
                    </div>
                    <FormWatcher name={`${name}.${index}.description_long`}>
                        {(value) =>
                            // Auch ohne den Erweitert-Schalter sichtbar, sobald etwas drinsteht — sonst
                            // landet die vom Produkt übernommene Beschreibung unsichtbar auf der Rechnung.
                            showDescription || !!value ? (
                                <div className="tw-mt-2">
                                    {/* @ts-ignore */}
                                    <FormInput.Textarea
                                        {...inputProps}
                                        containerClassName="!tw-mb-0"
                                        placeholder={t('invoiceForm.fields.descriptionLong')}
                                        name={`${name}.${index}.description_long`}
                                        disabled={!allowEditing}
                                    />
                                </div>
                            ) : null
                        }
                    </FormWatcher>
                    {showLedgerFields && (
                        <div className="tw-grid tw-grid-cols-3 tw-gap-4">
                            <CostCenterFormInput
                                disabled={!allowEditing}
                                name={`${name}.${index}.user_invoice_cost_1`}
                                labelRight="KS1"
                            />
                            <CostCenterFormInput
                                disabled={!allowEditing}
                                name={`${name}.${index}.user_invoice_cost_2`}
                                labelRight="KS2"
                            />
                            <AccountFormInput
                                disabled={!allowEditing}
                                name={`${name}.${index}.user_invoice_account_number`}
                                labelRight="SK"
                            />
                        </div>
                    )}
                </div>
            ))}
            {error && <span className="tw-text-xs tw-text-error">{error}</span>}
            <Button
                disabled={!allowEditing}
                size="xs"
                type="button"
                block
                icon={<FontAwesomeIcon icon={faPlus} />}
                className="tw-mt-2"
                onClick={() =>
                    append(
                        {
                            amount: '1'
                        },
                        { shouldFocus: true }
                    )
                }
            >
                {t('invoiceForm.itemsFields.addItem')}
            </Button>
        </>
    );
};
